import {Component, inject} from '@angular/core';
import {
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  InputContainerComponent,
  NewsletterSubscribeComponent,
  ToastService,
  EditableTextDirective
} from "@components";
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from "@angular/material/expansion";
import {BaseSignupPageComponent} from "../artist-home-page/artist-home-page.component";
import {AuthService} from "@api-services";
import {Router} from "@angular/router";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {Observable, of} from "rxjs";
import {SignupDto} from "@api-contracts";
import {environment} from "../../../../environments/environment";
import {LayoutService} from "@services";

@Component({
  selector: 'wun-collector-home-page',
  standalone: true,
  imports: [
    ButtonComponent,
    CheckboxComponent,
    InputComponent,
    InputContainerComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    NewsletterSubscribeComponent,
    FormsModule,
    ReactiveFormsModule,
    EditableTextDirective
],
  templateUrl: './collector-home-page.component.html',
  styleUrls: ['./collector-home-page.component.scss', '../base-user-page/base-user-page.component.scss']
})
export class CollectorHomePageComponent extends BaseSignupPageComponent {
  layoutService = inject(LayoutService);
  authService = inject(AuthService);
  toastService = inject(ToastService);
  router = inject(Router);

  // Fallback texts for editable translations
  readonly fallbacks = {
    header: {
      title: 'Discover art that speaks to you',
      subtitle: 'Explore and collect curated contemporary artworks from artists worldwide'
    },
    joinButton: 'Join Now',
    whyJoin: {
      title: 'Why join Wundart?',
      features: [
        {
          title: 'Personalized art recommendations',
          content: 'Wundart\'s Curatorial Team and sophisticated algorithm provide tailor-made suggestions that match your artistic taste. Discover artworks and artists that truly resonate with your personal preferences.'
        },
        {
          title: 'Direct connection with artists',
          content: 'Contact artists directly through Wundart\'s built-in messaging system. Build relationships, ask questions, and engage with the creators of the artworks you love.'
        },
        {
          title: 'Curated virtual collections',
          content: 'Showcase the artworks you\'ve purchased in a virtual collection on your profile. Track and display your art in a professional, organized way, and share it with other art lovers.'
        },
        {
          title: 'Free collector profile',
          content: 'At Wundart, we believe that discovering and collecting contemporary art should be accessible to everyone. That\'s why creating your collector profile is completely free.'
        },
        {
          title: 'Certified authenticity of artworks',
          content: 'Every piece comes with a certificate of authenticity, guaranteeing provenance and assuring the value of your purchase. Collect with confidence knowing your artworks are genuine.'
        },
        {
          title: 'Trusted and transparent purchase process',
          content: 'Wundart guides you step by step through secure payments via PayPal or bank transfer. Delivery fees and timelines are clearly communicated before final purchase, ensuring full transparency.'
        },
        {
          title: 'Safe and insured delivery',
          content: 'We ensure professional handling and full insurance of your artworks from gallery to your door. Receive your purchases safely and efficiently, anywhere in the covered destinations.'
        },
        {
          title: 'Flexible return policy',
          content: 'You can request a refund within 14 days of receiving your artwork if needed. After this period, payment will be processed to the artist and returns will no longer be possible.'
        }
      ]
    },
    questions: {
      title: 'Have more questions?',
      faqButton: 'Check out our FAQ',
      exchangePolicy: 'Exchange & Refund Policy'
    },
    joinNow: {
      title: 'Join Now',
      description: 'Create your personal account to save your favorite artworks, receive email updates and get exclusive offers.'
    },
    form: {
      name: 'Name',
      surname: 'Surname',
      email: 'Email',
      phone: 'Phone number',
      country: 'Country',
      city: 'City',
      username: 'Username',
      password: 'Your password',
      confirmPassword: 'Confirm your password',
      termsPrefix: 'By registering, you agree to our',
      termsLink: 'Terms of Service',
      termsAnd: 'and',
      privacyLink: 'Privacy Policy',
      newsletterConsent: 'I agree to receive updates, special offers and news from Wundart.',
      send: 'Send'
    },
    faq: {
      q1: {
        question: 'HOW DOES WUNDART WORK FOR A COLLECTOR?',
        content: 'Wundart provides a highly efficient and trusted e-commerce service that connects artists and potential buyers. Our Curatorial Team and a highly sophisticated algorithm ensure that you always receive tailor-made recommendations that fit your artistic criteria.',
        content2: 'Thanks to our partnership with Mailboxes Etc. Wundart is able to deliver your chosen artworks right at your door with a highly efficient and trusted delivery service.'
      },
      q2: {
        question: 'HOW DOES THE COLLECTOR PROFILE WORK?',
        content: 'As a potential buyer or simply an art lover, once you create your "Collector" profile, you will be able to receive custom suggestions of artists and artworks based on your personal tastes, follow and contact artists directly, create a wishlist of artworks you wish to purchase and showcase on your profile the artworks you have already bought with Wundart in a virtual collection.'
      },
      q3: {
        question: 'IS THE COLLECTOR PROFILE FREE?',
        content: 'Yes, the collector profile is entirely free and includes all the aforementioned features.'
      },
      q4: {
        question: 'HOW DOES THE PURCHASE PROCESS WORK ON WUNDART?',
        content: 'Once you have selected your chosen artwork Wundart will guide you through the payment process.',
        content2: 'The platform accepts payments with PayPal and bank transfers.',
        content3: 'The artwork will be issued with a certificate of authenticity that will ensure the provenance of the artwork.',
        content4: 'The delivery time will be estimated before the final purchase and will be available beforehand to ensure maximum transparency. The delivery fees will be charged to the buyer and always included in the final price of the order.'
      },
      q5: {
        question: 'IS THERE A RETURN POLICY FOR PURCHASED ITEMS?',
        content: 'Yes, Wundart provides a refund of a purchased item within 14 days from its purchase date, after which the payment to the artist will be processed and it won\'t be possible to return the item anymore.'
      },
      q6: {
        question: 'ARE THE SHIPPED ITEMS ENSURED?',
        content: 'Yes, Wundart\'s partner Mailboxes Etc. provides a full insurance for the artworks that they take charge of.'
      },
      q7: {
        question: 'CAN I CONTACT ARTISTS DIRECTLY?',
        content: 'Yes, Wundart has an efficient built-in system of messaging that allows the artists and the collectors to be directly connected.'
      },
      q8: {
        question: 'WHERE IS WUNDART AVAILABLE FOR POTENTIAL BUYERS?',
        content: 'At the moment Wundart\'s services are available for all potential buyers within the European Union, Switzerland and the United Kingdom. More areas of the world will be covered very soon.'
      },
      q9: {
        question: 'WHY SHOULD I BUY ARTWORKS WITH WUNDART?',
        content: 'Wundart provides a high quality service that is tailored for your needs and provides transparency and efficiency in every step of the buying process. The complete insurance of the artworks, the high level and trusted shipping service and the possibility to contact artists directly makes for a truly unique experience for an art collector.'
      }
    }
  };

  constructor() {
    super();
    if (environment.env === 'local') {
      this.signupForm.setValue({
        username: 'collector1',
        password: 'Password123!',
        confirmPassword: 'Password123!',
        city: 'Rome',
        country: 'Italy',
        email: 'simonedisante@gmail.com',
        firstName: 'Simone',
        lastName: 'Disante',
        phone: '+391234567890',
        profileType: 'COLLECTOR',
        termsAndConditions: true,
        newsletterConsent: true
      })
    }
  }

  signupForm = new FormGroup({
    username: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
    confirmPassword: new FormControl('', [Validators.required]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    city: new FormControl('', [Validators.required]),
    country: new FormControl('', [Validators.required]),
    phone: new FormControl('', [Validators.required]),
    profileType: new FormControl<'ARTIST' | 'COLLECTOR'>('COLLECTOR', [Validators.required]),
    termsAndConditions: new FormControl(false, [Validators.requiredTrue]),
    newsletterConsent: new FormControl(false),
  }, {
    validators: [
      (form) => this.passwordMatchValidator(form)
    ]
  });

  preSignup(): Observable<(signupDto: SignupDto) => void> {
    return of((signupDto: SignupDto) => {
    })
  }

  validate(): boolean {
    return true;
  }
}
